import React, { useEffect } from "react";
import { Phone } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const FAQS = [
  {
    q: "Where exactly is Auracrest Haven located?",
    a: "Auracrest Haven is located at Building No. 5/283, Anachal, Sengulam, Muthuvankudy, Munnar, Kerala 685565 — just minutes from the scenic Sengulam Dam reservoir, surrounded by cardamom and nutmeg plantations in the Western Ghats.",
  },
  {
    q: "How far is Auracrest Haven from the busy Munnar town?",
    a: "Auracrest Haven is approximately 18-20 km from Munnar town centre, about a 35-40 minute drive through beautiful ghat roads and tea plantations. We can arrange pick-up from Munnar town on request.",
  },
  {
    q: "Is Auracrest Haven suitable for families with children?",
    a: "Absolutely! Auracrest Haven is a family-friendly luxury homestay. Children love the open plantation grounds, the nature walks, and the chance to spot wildlife. Our spacious rooms comfortably accommodate families, and our kitchen can prepare child-friendly meals on request.",
  },
  {
    q: "Is Auracrest Haven good for a honeymoon or couples retreat?",
    a: "Yes — Auracrest Haven is one of the most romantic stays near Munnar. The private balconies with forest views, candlelit dining at SpyzzZ Restaurant, sunrise viewpoints, and the serene Sengulam Dam setting make it a perfect couples retreat and honeymoon destination in Kerala.",
  },
  {
    q: "What activities and attractions are available near Auracrest Haven?",
    a: "Guests can enjoy jeep safaris, zip-lining, hot air balloon rides, Kathakali and Kalaripayattu performances, tea and spice garden tours, boating at Sengulam Dam, Kolukkumalai sunrise treks, wildlife safaris, waterfall visits, and scenic Munnar ghat road drives — all bookable through the resort.",
  },
  {
    q: "What is the best time to visit Auracrest Haven and Munnar?",
    a: "Munnar is beautiful year-round. September to May is ideal for clear skies and outdoor activities. The monsoon season (June-August) transforms the hills into a lush green paradise — perfect for nature lovers. Winters (December-February) bring cool misty mornings ideal for Kolukkumalai sunrise treks.",
  },
  {
    q: "Does Auracrest Haven have a restaurant?",
    a: "Yes — SpyzzZ Restaurant at Auracrest Haven serves authentic Kerala cuisine in an open-air setting with stunning forest views. We also offer a home delivery and takeaway service (Spyzzie) for guests and visitors in the Munnar area.",
  },
  {
    q: "How do I book a stay at Auracrest Haven?",
    a: "You can book directly by calling or WhatsApp-ing us at +91-9388881016, emailing booking@auracresthaven.com, or using the enquiry form on this page. Direct bookings get the best rates with no third-party commission.",
  },
  {
    q: "Is Auracrest Haven an eco-friendly resort?",
    a: "Yes. Auracrest Haven is built within a working cardamom and nutmeg plantation, using natural materials and sustainable practices. We minimise plastic use, support local farmers, and our dining uses fresh locally-sourced ingredients — making it a true eco-resort experience in Munnar.",
  },
  {
    q: "Is there free and convenient parking available at Auracrest Haven?",
    a: "Yes, Auracrest Haven has on-site parking for guests arriving by car or self-drive vehicle. The property is accessible by road and we can guide you with directions from Munnar town or the nearest highway.",
  },
];

const FAQ = () => {
  useEffect(() => {
    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    let s = document.getElementById("faq-jsonld");
    if (!s) {
      s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = "faq-jsonld";
      document.head.appendChild(s);
    }
    s.textContent = JSON.stringify(data);
    return () => {
      const el = document.getElementById("faq-jsonld");
      if (el) el.remove();
    };
  }, []);

  return (
  <section id="faq" className="py-20 md:py-28 bg-white">
    <div className="max-w-3xl mx-auto px-5 md:px-8">
      <div className="text-center mb-12">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#17a89b] mb-4">
          Frequently Asked Questions
        </p>
        <h2 className="text-3xl md:text-5xl font-extrabold text-neutral-900">
          Everything You Need to Know
        </h2>
      </div>

      <Accordion type="single" collapsible className="w-full space-y-3">
        {FAQS.map((item, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            className="border border-neutral-200 rounded-2xl px-5 bg-neutral-50"
          >
            <AccordionTrigger className="text-left text-base font-semibold text-neutral-900 hover:no-underline py-5">
              {item.q}
            </AccordionTrigger>
            <AccordionContent className="text-neutral-600 leading-7 text-sm pb-5">
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="text-center mt-10">
        <p className="text-neutral-600 mb-4">Still have questions? We're happy to help.</p>
        <a
          href="tel:+919388881016"
          className="inline-flex items-center gap-2 bg-[#17a89b] hover:bg-[#128a7f] text-white font-semibold px-8 py-3 rounded-md transition-colors"
        >
          <Phone className="w-4 h-4" /> Call us: +91-9388881016
        </a>
      </div>
    </div>
  </section>
  );
};

export default FAQ;
